// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-unsafe-return */

import moment from "moment";
import React from "react";
import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import ResultsTable from "../../../Lumber/Search/ResultsTable";
import { AnyFilter } from "../../../Lumber/Search/types";
import { FieldLogData } from "../../../../apis/types/fieldTypes";
import { useUserAuth } from "../../../../hooks/useUserAuth.js";
import { ApplicationArea } from "../../../Lumber/ApplicationAreas/index.js";

interface Props {
	loading: boolean;
	data: FieldLogData[];
	filters: { [key in keyof FieldLogData]?: AnyFilter };
	searchTerm: { term: string; cols: (keyof FieldLogData)[] };
	// setData: React.Dispatch<React.SetStateAction<FlightLogData[]>>;
}

export const FieldSearchResults = ({ data, loading, filters, searchTerm }: Props) => {
	const DATE_FORMAT = "MM/DD/YYYY";
	const moduleNavigation = useModuleNavigation();
	const userAuth = useUserAuth();

	const headers = {
		fieldName: "Field",
		farm: "Farm",
		// clientName: "Client",
		coopLocation: "Cooperator",
		cropName: "Crop",
		growingSeasonName: "Growing Season",
		plantDate: "Plant Date",
		plotDimensions: "Plots",
		plotSize: "Plot Size",
		lastFlightDate: "Last Flight Date",
		totalFlights: "Flights",
		trials: "Trials",
	};

	return (
		<ResultsTable<FieldLogData>
			level="field"
			loading={loading}
			data={data}
			headers={headers}
			onRenderDataColumn={{
				farm: (f) => ({ render: f.farmAbbr, options: { title: f.farmName } }),
				coopLocation: ({ cooperatorName, city, region, countryCode }) => ({
					render: [cooperatorName, `${city}, ${region}, ${countryCode}`],
					options: { breaks: true },
				}),
				plantDate: (pd) => (pd ? moment(pd).format(DATE_FORMAT) : undefined),
				plotDimensions: ({ numOfColumns, numOfRanges }) => ({
					render: `${numOfColumns * numOfRanges}`,
					options: { title: `${numOfColumns} \u00D7 ${numOfRanges}` },
				}),
				lastFlightDate: (lfd) => (lfd ? moment(lfd).format(DATE_FORMAT) : undefined),
				plotSize: (ps) => `${Math.round(ps.plotWidth * 1000) / 1000} \u00D7 ${Math.round(ps.plotLength * 1000) / 1000}`,
				trials: (ts) => ts?.map((t) => t.trialName) ?? "",
			}}
			onSortColumn={{
				fieldName: true,
				farm: (a, b) => a.farmName.localeCompare(b.farmName),
				coopLocation: (a, b) =>
					(a.cooperatorName ?? "").localeCompare(b.cooperatorName ?? "") ||
					(a.country ?? "").localeCompare(b.country ?? "") ||
					(a.region ?? "").localeCompare(b.region ?? "") ||
					(a.city ?? "").localeCompare(b.city ?? ""),
				cropName: true,
				growingSeasonName: true,
				plantDate: (a, b) => moment(b).diff(moment(a)),
				plotDimensions: (a, b) => b.numOfColumns * b.numOfRanges - a.numOfColumns * a.numOfRanges,
				totalFlights: (a, b) => b - a,
				lastFlightDate: (a, b) => moment(b).diff(moment(a)),
			}}
			columnFilters={filters}
			actions={[
				{
					hasArea: (row) => userAuth.hasApplicationArea(ApplicationArea.FieldEdit, row.clientId),
					title: "Edit",
					icon: "pencil",
					to: (row) => moduleNavigation.createFieldLink(false, row.fieldId, "edit"),
				},
				{
					hasArea: (row) => userAuth.hasApplicationArea(ApplicationArea.FieldHeatMap, row.clientId),
					title: "Heatmap",
					icon: "map",
					to: (row) => moduleNavigation.createFieldLink(false, row.fieldId, "heatmap"),
				},
				{
					hasArea: (row) => userAuth.hasApplicationArea(ApplicationArea.FieldEdit, row.clientId),
					title: "Outline",
					icon: "table",
					to: (row) => moduleNavigation.createFieldLink(false, row.fieldId, "outline"),
				},
			]}
			links={{
				farm: (f) => moduleNavigation.createFarmLink(false, f.farmId, "viewer"),
				fieldName: (f) => moduleNavigation.createFieldLink(false, f.fieldId),
				trials: (t) =>
					moduleNavigation.createTrialLink(false, t.trialId, t.fieldScaleTrial ? "geo-mfa-explorer" : "viewer"),
			}}
			searchTerm={searchTerm}
		/>
	);
};
